import type { NewRequisitionForm } from "../../recoil/types";

/**
 * Fetal Antigen Configuration Constants
 *
 * These arrays define the configuration for RBC and Platelet fetal antigens
 * displayed in the order form. They are used for rendering checkboxes and
 * managing state across multiple components.
 *
 * isOriginal: Indicates antigens that existed before the Fetal Antigen Expansion feature.
 *             These are always visible regardless of the feature flag state.
 *             When isOriginal is false, the antigen is only shown when the
 *             `fetalAntigenExpansion` feature flag is enabled.
 *
 * TODO: Once the fetalAntigenExpansion feature flag is permanently enabled and removed,
 *       the `isOriginal` property can be deleted from all antigen configs.
 */

export const RBC_GROUP_NAME = "Red Blood Cell (RBC) Fetal Antigen(s)";
export const PLATELET_GROUP_NAME = "Platelet Fetal Antigen(s)";

export interface AntigenConfig {
  name: keyof NewRequisitionForm;
  label: string;
  sublabel?: string;
  sfPanelValue: string;
  isOriginal: boolean;
  disabledWhenRhd?: boolean;
}

export interface PlateletAntigenConfig extends AntigenConfig {
  isPrimary?: boolean;
}

export type FetalAntigenFieldSelection = Partial<
  Record<AntigenConfig["name"], boolean>
>;

/**
 * RBC (Red Blood Cell) Antigens
 * Original 6 antigens: C, c, D, E, Fya, K
 * New 10 antigens (behind feature flag): e, Fyb, Jka, Jkb, k, M, N, S, s, U
 */
export const RBC_ANTIGENS: AntigenConfig[] = [
  {
    name: "isFetalAntigenBigC",
    label: "C",
    sublabel: "(Big C)",
    sfPanelValue: "big C",
    isOriginal: true,
  },
  {
    name: "isFetalAntigenLittleC",
    label: "c",
    sublabel: "(little c)",
    sfPanelValue: "little C",
    isOriginal: true,
  },
  {
    name: "isFetalAntigenD",
    label: "D",
    sfPanelValue: "D Antigen",
    isOriginal: true,
    disabledWhenRhd: true,
  },
  {
    name: "isFetalAntigenE",
    label: "E",
    sublabel: "(Big E)",
    sfPanelValue: "big E",
    isOriginal: true,
  },
  {
    name: "isFetalAntigenLittleE",
    label: "e",
    sublabel: "(little e)",
    sfPanelValue: "little E",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenDuffy",
    label: "Fya (Duffy)",
    sfPanelValue: "Duffy (FyA)",
    isOriginal: true,
  },
  {
    name: "isFetalAntigenFyb",
    label: "Fyb (Duffy)",
    sfPanelValue: "Duffy (FyB)",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenJka",
    label: "Jka (Kidd)",
    sfPanelValue: "Jka",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenJkb",
    label: "Jkb (Kidd)",
    sfPanelValue: "Jkb",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenKell",
    label: "K (Kell)",
    sfPanelValue: "big K (Kell)",
    isOriginal: true,
  },
  {
    name: "isFetalAntigenLittleK",
    label: "k",
    sublabel: "(little k)",
    sfPanelValue: "little K",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenM",
    label: "M",
    sfPanelValue: "M Antigen",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenN",
    label: "N",
    sfPanelValue: "N Antigen",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenBigS",
    label: "S",
    sublabel: "(Big S)",
    sfPanelValue: "Big S",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenLittleS",
    label: "s",
    sublabel: "(little s)",
    sfPanelValue: "little S",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenU",
    label: "U",
    sfPanelValue: "U Antigen",
    isOriginal: false,
  },
];

/**
 * Platelet Antigens (HPA - Human Platelet Antigens)
 * All platelet antigens are new (isOriginal: false) and only shown when
 * the fetalAntigenExpansion feature flag is enabled.
 *
 * isPrimary: HPA-1a is shown at the top level; others are in the collapsible section.
 */
export const PLATELET_ANTIGENS: PlateletAntigenConfig[] = [
  {
    name: "isFetalAntigenHpa1a",
    label: "HPA-1a",
    sfPanelValue: "HPA-1a",
    isOriginal: false,
    isPrimary: true,
  },
  {
    name: "isFetalAntigenHpa1b",
    label: "HPA-1b",
    sfPanelValue: "HPA-1b",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa2a",
    label: "HPA-2a",
    sfPanelValue: "HPA-2a",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa2b",
    label: "HPA-2b",
    sfPanelValue: "HPA-2b",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa3a",
    label: "HPA-3a",
    sfPanelValue: "HPA-3a",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa3b",
    label: "HPA-3b",
    sfPanelValue: "HPA-3b",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa4a",
    label: "HPA-4a",
    sfPanelValue: "HPA-4a",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa4b",
    label: "HPA-4b",
    sfPanelValue: "HPA-4b",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa5a",
    label: "HPA-5a",
    sfPanelValue: "HPA-5a",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa5b",
    label: "HPA-5b",
    sfPanelValue: "HPA-5b",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa9a",
    label: "HPA-9a",
    sfPanelValue: "HPA-9a",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa9b",
    label: "HPA-9b",
    sfPanelValue: "HPA-9b",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa15a",
    label: "HPA-15a",
    sfPanelValue: "HPA-15a",
    isOriginal: false,
  },
  {
    name: "isFetalAntigenHpa15b",
    label: "HPA-15b",
    sfPanelValue: "HPA-15b",
    isOriginal: false,
  },
];

/**
 * Check if any RBC antigen is selected in the form
 */
export const hasAnyRbcAntigen = (form: NewRequisitionForm): boolean =>
  RBC_ANTIGENS.some((antigen) => Boolean(form[antigen.name]));

/**
 * Check if any Platelet antigen is selected in the form
 */
export const hasAnyPlateletAntigen = (form: NewRequisitionForm): boolean =>
  PLATELET_ANTIGENS.some((antigen) => Boolean(form[antigen.name]));

export const getSelectedFetalAntigenPanel = (
  form: Partial<Record<keyof NewRequisitionForm, unknown>>,
): string[] =>
  [...RBC_ANTIGENS, ...PLATELET_ANTIGENS]
    .filter((antigen) => Boolean(form[antigen.name]))
    .map((antigen) => antigen.sfPanelValue);

export const getFetalAntigenFieldSelection = (
  form: Partial<Record<keyof NewRequisitionForm, unknown>>,
): FetalAntigenFieldSelection =>
  Object.fromEntries(
    [...RBC_ANTIGENS, ...PLATELET_ANTIGENS].map((antigen) => [
      antigen.name,
      Boolean(form[antigen.name]),
    ]),
  );

export const isFetalAntigenSelectionValid = (
  antigens: string[],
  isRbc: boolean,
  isPlatelet: boolean,
): boolean =>
  (isRbc || isPlatelet) &&
  (!isRbc ||
    RBC_ANTIGENS.some((antigen) => antigens.includes(antigen.sfPanelValue))) &&
  (!isPlatelet ||
    PLATELET_ANTIGENS.some((antigen) =>
      antigens.includes(antigen.sfPanelValue),
    ));

type FetalAntigenFileValues = Record<
  string,
  {
    fileName: string;
    valid: boolean;
  }
>;

export const hasSuccessfulFetalAntigenUpload = (
  fileUrls: FetalAntigenFileValues,
): boolean =>
  Object.values(fileUrls).some(
    ({ fileName, valid }) => valid && Boolean(fileName),
  );

export const isFetalAntigenDocumentSatisfied = (
  requireFile: boolean | undefined,
  fileUrls: FetalAntigenFileValues,
): boolean =>
  requireFile === false ||
  (requireFile === true && hasSuccessfulFetalAntigenUpload(fileUrls));

export const CLEARED_RBC_ANTIGEN_FIELDS = Object.fromEntries(
  RBC_ANTIGENS.map((a) => [a.name, false]),
) as Partial<NewRequisitionForm>;

export const CLEARED_ANTIGEN_FIELDS = Object.fromEntries([
  ...Object.entries(CLEARED_RBC_ANTIGEN_FIELDS),
  ...PLATELET_ANTIGENS.map((a) => [a.name, false]),
]) as Partial<NewRequisitionForm>;

/** Cleared when Aneuploidy NIPT is off or a Unity Complete preset is applied. */
export const CLEARED_ANEUPLOIDY_ADD_ONS: Partial<NewRequisitionForm> = {
  is22q: false,
  aneuploidyIncludeRhd: false,
  aneuploidyOptOutFetalSex: false,
  isFetalAntigen: false,
  isFetalAntigenRbc: false,
  isFetalAntigenPlatelet: false,
  ...CLEARED_ANTIGEN_FIELDS,
};
